import React from 'react';
import { AlertTriangle, RotateCcw, WifiOff, Clock, ShieldAlert, X } from 'lucide-react';
import { cn } from '../../lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT ERROR TURN
//
// A failure that happened *inside the conversation* belongs in the conversation.
// A toast for this is the wrong container twice over: it disappears after four
// seconds, and it leaves the transcript ending on the user's prompt with no
// explanation of why nothing answered it.
//
// So a failed turn renders as a real turn — same rail, same column — carrying
// the actual reason and the action that addresses it.
//
// It uses the red-500 family rather than the `destructive` token: that token is
// 30% lightness, which is all but invisible on this background. Every other
// warning surface in the app (ToolSkeletonCard, the conflict chips) made the
// same call.
// ═══════════════════════════════════════════════════════════════════════════════

const KIND_ICON = {
    offline: WifiOff,
    network: WifiOff,
    timeout: Clock,
    auth: ShieldAlert,
    rate_limit: Clock,
};

export function ChatErrorTurn({
    error,
    onRetry,
    onDismiss,
    onReload,
    className,
    compact = false,
}) {
    if (!error) return null;

    const Icon = KIND_ICON[error.kind] || AlertTriangle;
    const canRetry = error.retryable !== false && !!onRetry;

    return (
        <div
            className={cn(
                'min-w-0 rounded-xl border border-red-500/25 bg-gradient-to-r from-red-500/[0.09] to-rose-600/[0.03] p-3.5 backdrop-blur-sm',
                className
            )}
            role="alert"
            data-testid="chat-error-turn"
            data-error-kind={error.kind}
        >
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-red-500/30 bg-red-500/15">
                    <Icon className="h-4 w-4 text-red-400" />
                </div>

                <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-red-400/80">
                        {error.kind === 'offline' ? 'Offline' : 'Failed'}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-foreground">{error.title}</p>
                    {error.detail && (
                        <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{error.detail}</p>
                    )}

                    {!compact && (canRetry || onReload || onDismiss) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {canRetry && (
                                <button
                                    onClick={onRetry}
                                    data-testid="chat-error-retry"
                                    className="flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/15 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-red-200 transition-colors hover:bg-red-500/25 hover:text-white"
                                >
                                    <RotateCcw className="h-3 w-3" />
                                    Try again
                                </button>
                            )}
                            {onReload && (
                                <button
                                    onClick={onReload}
                                    data-testid="chat-error-reload"
                                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                                >
                                    <RotateCcw className="h-3 w-3" />
                                    Reload conversation
                                </button>
                            )}
                            {onDismiss && (
                                <button
                                    onClick={onDismiss}
                                    data-testid="chat-error-dismiss"
                                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                                >
                                    <X className="h-3 w-3" />
                                    Dismiss
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ChatErrorTurn;
